import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { renderBlocks } from '../../renderer/BlockRenderer.js';
import { editBlockAttrs } from '../../renderer/editAttrs.js';
import { visibilityField } from '../_shared/blockFields.js';
import { num, str, visibilityCss } from '../_shared/blockRender.js';
import { isThemeColorToken } from '../../theme/colorTokens.js';
import { ANIM_ITEM } from '../../sections/_shared/universal.js';
/**
 * Shrine `column` — the container block of the **Custom columns V2** (`custom-columns-new`) section, ported
 * config-by-config from `blocks/column.liquid`. A column spans a number of the parent grid's PARTS on
 * desktop (1–12 of `--desktop-grid-parts`) and on mobile (1–4 of `--mobile-grid-parts`); the width is a
 * `calc(100% / total-parts * width)` driven by inline `--desktop-width` / `--mobile-width` vars that the
 * section's scoped stylesheet reads. Inside, a single `.content-wrapper.block-margins` slot holds the
 * column's CHILD blocks (heading / text / image / icon-with-content …) rendered by the recursive
 * {@link renderBlocks} — Shrine's `content_for 'blocks'`.
 *
 * Chrome (all optional, Shrine parity): a SOLID or GRADIENT background (with opacity), a SOLID or GRADIENT
 * border (thickness + style + opacity), a drop SHADOW (color/opacity/offsets/spread/blur), corner radius
 * (all-equal or per-corner), and desktop/mobile inner padding. The border/bg/shadow chrome is expressed as
 * the exact Shrine CSS custom properties (`--bg-color`, `--border-*`, `--shadow-*`, `--padding-*`); the
 * matching rules (`.custom-columns__column > .content-wrapper`, `.bg-gradient`, `.border-gradient`,
 * `.block-shadow` pseudo-elements) live once in the section's scoped `<style>`, so this block only emits the
 * DOM + the per-column inline vars — byte-faithful to Shrine's markup. Visibility (Display on) is scoped per
 * instance and reflows in both the live storefront (`@media`) and the builder device-preview (`@container`).
 *
 * Padding vars follow the repo's Shrine convention (px, not Shrine's rem÷10): the seller's px value is used
 * directly so a 15px setting reads as 15px regardless of the root font-size.
 */
const BORDER_STYLE_OPTIONS = [
    { value: 'solid', label: 'Solid' },
    { value: 'dashed', label: 'Dashed' },
];
const COLOR_TYPE_OPTIONS = [
    { value: 'solid', label: 'Solid' },
    { value: 'gradient', label: 'Gradient' },
];
const DEFAULT_GRADIENT = 'linear-gradient(57deg, rgba(249, 119, 255, 1) 8%, rgba(54, 212, 255, 1) 47%, rgba(43, 252, 135, 1) 83%)';
export const columnBlockSchema = {
    type: 'column',
    name: 'Column',
    // A column is a CONTAINER: it hosts the generic content blocks in its single slot (Shrine `column.liquid`
    // `blocks: [...]`). We wire the subset shipped as registered blocks today; the rest are added as they land.
    accepts: ['heading', 'text', 'image', 'icon-with-content', 'icon-with-text', 'button', 'divider'],
    fields: [
        {
            id: 'desktopWidth',
            type: 'range',
            label: 'Desktop width',
            min: 1,
            max: 12,
            step: 1,
            default: 3,
        },
        {
            id: 'mobileWidth',
            type: 'range',
            label: 'Mobile width',
            min: 1,
            max: 4,
            step: 1,
            default: 4,
        },
        visibilityField,
        // ── Column style ────────────────────────────────────────────────────────────────────────────────
        {
            id: 'bgType',
            type: 'select',
            label: 'Background',
            group: 'Column style',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'solid', label: 'Solid color' },
                { value: 'gradient', label: 'Gradient color' },
            ],
        },
        {
            id: 'bgColor',
            type: 'color',
            label: 'Background color',
            group: 'Column style',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'bgType', equals: 'solid' },
        },
        {
            id: 'bgGradient',
            type: 'gradient',
            label: 'Background gradient color',
            group: 'Column style',
            default: DEFAULT_GRADIENT,
            visibleWhen: { field: 'bgType', equals: 'gradient' },
        },
        {
            id: 'bgOpacity',
            type: 'range',
            label: 'Background opacity',
            group: 'Column style',
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 100,
            visibleWhen: { field: 'bgType', notEquals: 'none' },
        },
        {
            id: 'borderWidth',
            type: 'range',
            label: 'Border thickness',
            group: 'Column style',
            min: 0,
            max: 12,
            step: 1,
            unit: 'px',
            default: 0,
        },
        {
            id: 'borderStyle',
            type: 'select',
            label: 'Border style',
            group: 'Column style',
            default: 'solid',
            options: BORDER_STYLE_OPTIONS,
            visibleWhen: { field: 'borderWidth', notEquals: 0 },
        },
        {
            id: 'borderColorType',
            type: 'select',
            label: 'Border color type',
            group: 'Column style',
            default: 'solid',
            options: COLOR_TYPE_OPTIONS,
            visibleWhen: { field: 'borderWidth', notEquals: 0 },
        },
        {
            id: 'borderColor',
            type: 'color',
            label: 'Border color',
            group: 'Column style',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'borderColorType', equals: 'solid' },
        },
        {
            id: 'borderGradient',
            type: 'gradient',
            label: 'Border gradient color',
            group: 'Column style',
            default: DEFAULT_GRADIENT,
            visibleWhen: { field: 'borderColorType', equals: 'gradient' },
        },
        {
            id: 'borderOpacity',
            type: 'range',
            label: 'Border opacity',
            group: 'Column style',
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 100,
            visibleWhen: { field: 'borderWidth', notEquals: 0 },
        },
        // ── Shadow ──────────────────────────────────────────────────────────────────────────────────────
        {
            id: 'enableShadow',
            type: 'checkbox',
            label: 'Enable drop shadow',
            group: 'Column style',
            default: false,
        },
        {
            id: 'shadowColor',
            type: 'color',
            label: 'Shadow color',
            group: 'Column style',
            default: { ref: 'custom', value: '#000000' },
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowOpacity',
            type: 'range',
            label: 'Shadow opacity',
            group: 'Column style',
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 15,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowHorizontalOffset',
            type: 'range',
            label: 'Shadow horizontal offset',
            group: 'Column style',
            min: -50,
            max: 50,
            step: 2,
            unit: 'px',
            default: 2,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowVerticalOffset',
            type: 'range',
            label: 'Shadow vertical offset',
            group: 'Column style',
            min: -50,
            max: 50,
            step: 2,
            unit: 'px',
            default: 6,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowSpread',
            type: 'range',
            label: 'Shadow spread',
            group: 'Column style',
            min: -50,
            max: 50,
            step: 2,
            unit: 'px',
            default: 0,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        {
            id: 'shadowBlur',
            type: 'range',
            label: 'Shadow blur',
            group: 'Column style',
            min: 0,
            max: 100,
            step: 2,
            unit: 'px',
            default: 16,
            visibleWhen: { field: 'enableShadow', equals: true },
        },
        // ── Corner radius ───────────────────────────────────────────────────────────────────────────────
        {
            id: 'roundCornersEqually',
            type: 'checkbox',
            label: 'Round all corners equally',
            group: 'Column style',
            default: true,
        },
        {
            id: 'borderRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Column style',
            min: 0,
            max: 50,
            step: 2,
            unit: 'px',
            default: 12,
            visibleWhen: { field: 'roundCornersEqually', equals: true },
        },
        {
            id: 'borderRadiusTopLeft',
            type: 'range',
            label: 'Top-left corner radius',
            group: 'Column style',
            min: 0,
            max: 50,
            step: 2,
            unit: 'px',
            default: 12,
            visibleWhen: { field: 'roundCornersEqually', equals: false },
        },
        {
            id: 'borderRadiusTopRight',
            type: 'range',
            label: 'Top-right corner radius',
            group: 'Column style',
            min: 0,
            max: 50,
            step: 2,
            unit: 'px',
            default: 12,
            visibleWhen: { field: 'roundCornersEqually', equals: false },
        },
        {
            id: 'borderRadiusBottomRight',
            type: 'range',
            label: 'Bottom-right corner radius',
            group: 'Column style',
            min: 0,
            max: 50,
            step: 2,
            unit: 'px',
            default: 12,
            visibleWhen: { field: 'roundCornersEqually', equals: false },
        },
        {
            id: 'borderRadiusBottomLeft',
            type: 'range',
            label: 'Bottom-left corner radius',
            group: 'Column style',
            min: 0,
            max: 50,
            step: 2,
            unit: 'px',
            default: 12,
            visibleWhen: { field: 'roundCornersEqually', equals: false },
        },
        // ── Padding ─────────────────────────────────────────────────────────────────────────────────────
        {
            id: 'verticalDesktopPadding',
            type: 'range',
            label: 'Vertical desktop padding',
            group: 'Padding',
            min: 0,
            max: 60,
            step: 3,
            unit: 'px',
            default: 15,
        },
        {
            id: 'horizontalDesktopPadding',
            type: 'range',
            label: 'Horizontal desktop padding',
            group: 'Padding',
            min: 0,
            max: 60,
            step: 3,
            unit: 'px',
            default: 15,
        },
        {
            id: 'verticalMobilePadding',
            type: 'range',
            label: 'Vertical mobile padding',
            group: 'Padding',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 14,
        },
        {
            id: 'horizontalMobilePadding',
            type: 'range',
            label: 'Horizontal mobile padding',
            group: 'Padding',
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
            default: 14,
        },
    ],
};
/** Read a stored `color` ColorValue → its hex (custom) or a literal string, else the fallback. */
function colorHex(v, fallback) {
    if (v && typeof v === 'object') {
        const c = v;
        if (c.ref === 'custom' && typeof c.value === 'string')
            return c.value;
        // 0019 §4a: the THEME branch. Without it a { ref:'theme', token } silently fell through to the
        // frozen fallback, so a seller re-theming their store changed nothing here (allowlisted, finding #18).
        if (c.ref === 'theme' && typeof c.token === 'string' && isThemeColorToken(c.token))
            return `var(--aqx-theme-${c.token})`;
    }
    if (typeof v === 'string' && v.length > 0)
        return v;
    return fallback;
}
/** #RRGGBB → { r, g, b }. Tolerates #RGB and a missing leading hash; falls back to black on garbage. */
function hexToRgb(hex) {
    let h = hex.trim().replace('#', '');
    if (h.length === 3)
        h = h.replace(/./g, (c) => c + c);
    const int = Number.parseInt(h, 16);
    if (!Number.isFinite(int) || h.length !== 6)
        return { r: 0, g: 0, b: 0 };
    return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}
/**
 * Shrine `color_modify: 'alpha', a` — return the colour unchanged at full opacity (Shrine keeps the hex, e.g.
 * the shipped `#2f5f8a`) and an `rgba()` below it. Keeps the default column border byte-faithful.
 *
 * A THEME TOKEN resolves to `var(--aqx-theme-*)`, which no hex arithmetic can read — `hexToRgb` would return
 * black and silently repaint the column. So a var takes the `color-mix` path instead (same idiom as
 * blocks/text-with-icon's `alphaColor`), which keeps BOTH the seller's theme colour and the opacity slider.
 */
function withAlpha(color, alpha) {
    if (alpha >= 1)
        return color;
    if (color.startsWith('var(')) {
        return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`;
    }
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
const ColumnBlock = ({ block, context }) => {
    const bs = block.settings;
    const cls = `aqx-col-${block.id}`;
    const children = (block.blocks ?? []);
    const bgType = str(bs.bgType, 'none');
    const borderWidth = num(bs.borderWidth, 0);
    const borderColorType = str(bs.borderColorType, 'solid');
    const enableShadow = bs.enableShadow === true;
    const roundEqually = bs.roundCornersEqually !== false; // default true
    // ── Per-column inline CSS custom properties — read by the section's scoped stylesheet. ──────────────
    const style = {
        '--border-style': str(bs.borderStyle, 'solid'),
        '--mobile-width': String(num(bs.mobileWidth, 4)),
        '--desktop-width': String(num(bs.desktopWidth, 3)),
        '--border-width': `${borderWidth}px`,
        '--border-radius': roundEqually
            ? `${num(bs.borderRadius, 12)}px`
            : `${num(bs.borderRadiusTopLeft, 12)}px ${num(bs.borderRadiusTopRight, 12)}px ${num(bs.borderRadiusBottomRight, 12)}px ${num(bs.borderRadiusBottomLeft, 12)}px`,
    };
    // Background (solid → rgba baked with opacity; gradient → the gradient string + a separate --bg-opacity).
    const bgOpacity = num(bs.bgOpacity, 100) / 100;
    if (bgType === 'solid') {
        style['--bg-color'] = withAlpha(colorHex(bs.bgColor, 'var(--aqx-theme-accent)'), bgOpacity);
    }
    else if (bgType === 'gradient') {
        style['--bg-color'] = str(bs.bgGradient, DEFAULT_GRADIENT);
        style['--bg-opacity'] = String(bgOpacity);
    }
    // Border color (solid → alpha-modified hex/rgba; gradient → the gradient string + --border-opacity).
    const borderOpacity = num(bs.borderOpacity, 100) / 100;
    if (borderColorType === 'solid') {
        style['--border-color'] = withAlpha(colorHex(bs.borderColor, 'var(--aqx-theme-accent)'), borderOpacity);
    }
    else if (borderColorType === 'gradient') {
        style['--border-color'] = str(bs.borderGradient, DEFAULT_GRADIENT);
        style['--border-opacity'] = String(borderOpacity);
    }
    // Shadow — vars drive `.block-shadow:after`; when off, Shrine pins the column's own box-shadow to none.
    if (enableShadow) {
        style['--shadow-color'] = withAlpha(colorHex(bs.shadowColor, '#000000'), num(bs.shadowOpacity, 15) / 100);
        style['--shadow-offset-x'] = `${num(bs.shadowHorizontalOffset, 2)}px`;
        style['--shadow-offset-y'] = `${num(bs.shadowVerticalOffset, 6)}px`;
        style['--shadow-blur'] = `${num(bs.shadowBlur, 16)}px`;
        style['--shadow-spread'] = `${num(bs.shadowSpread, 0)}px`;
    }
    else {
        style.boxShadow = 'none';
    }
    // Inner padding vars only when the column has chrome (Shrine: bg ≠ none OR border OR shadow); otherwise the
    // content-wrapper stays flush (its `padding: var(--padding-y…)` collapses when the vars are undefined).
    if (bgType !== 'none' || borderWidth > 0 || enableShadow) {
        style['--padding-y--mobile'] = `${num(bs.verticalMobilePadding, 14)}px`;
        style['--padding-x--mobile'] = `${num(bs.horizontalMobilePadding, 14)}px`;
        style['--padding-y--desktop'] = `${num(bs.verticalDesktopPadding, 15)}px`;
        style['--padding-x--desktop'] = `${num(bs.horizontalDesktopPadding, 15)}px`;
    }
    const classNames = [
        'custom-columns__column',
        cls,
        borderColorType === 'gradient' && borderWidth > 0 ? 'border-gradient' : '',
        bgType === 'gradient' ? 'bg-gradient' : '',
        enableShadow ? 'block-shadow' : '',
    ]
        .filter(Boolean)
        .join(' ');
    const css = visibilityCss(cls, bs.visibility);
    return (_jsxs(_Fragment, { children: [css ? _jsx("style", { dangerouslySetInnerHTML: { __html: css } }) : null, _jsx("div", { className: classNames, style: style, ...editBlockAttrs(block.id, context), ...ANIM_ITEM, children: _jsx("div", { className: "content-wrapper block-margins", children: renderBlocks(children, context) }) })] }));
};
export const columnBlock = {
    type: 'column',
    schema: columnBlockSchema,
    Component: ColumnBlock,
    meta: { label: 'Column' },
};
//# sourceMappingURL=index.js.map